import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Popper, MenuList, MenuItem } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { CAPTION_FONT_WEIGHT_LABELS, FONT_STYLE } from '@src/constants/dubbing';
import useFontCatalog from '@src/hooks/useFontCatalog';
import { findFaceUrl, loadCaptionFont } from '@src/services/caption';
import {
  StyledSelectTrigger,
  MENU_PAPER_SX,
  MENU_ITEM_SX,
} from './index.style';

// The submenu sits beside its family row, not under the trigger.
const SUBMENU_PAPER_SX = { ...MENU_PAPER_SX, ml: 0.5, mt: 0, minWidth: 120 };

const CHEVRON_SX = { ml: 'auto', color: 'rgba(0, 0, 0, 0.4)' };

// The submenu offers weights, upright only. Slant is the ITALIC chip's
// business and lives in `formats`, not in this menu.
const uprightWeights = (font) =>
  font.variants
    .filter(({ style }) => style === FONT_STYLE.NORMAL)
    .map(({ weight }) => weight);

const FontFamilySelect = ({
  width,
  family: selectedFamily,
  weight: selectedWeight,
  onChange,
}) => {
  const { t } = useTranslation();
  const fonts = useFontCatalog();
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const submenuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeFamily, setActiveFamily] = useState(null);
  const [submenuAnchor, setSubmenuAnchor] = useState(null);

  // Every option is drawn in its own face, so the faces have to be fetched —
  // but by level. Seven here for the family labels; one family's set below.
  // All 114 at once would be megabytes for a dropdown.
  useEffect(() => {
    fonts.forEach(({ family, defaultWeight }) => {
      const face = { fontFamily: family, fontWeight: defaultWeight };
      loadCaptionFont(face, findFaceUrl(fonts, face));
    });
  }, [fonts]);

  // One pass over the catalogue: the list needs each family's weights and so
  // does the submenu. Recomputing them per render would allocate on every hover.
  const families = useMemo(
    () => fonts.map((font) => ({ ...font, weights: uprightWeights(font) })),
    [fonts],
  );

  const activeWeights = useMemo(
    () => families.find(({ family }) => family === activeFamily)?.weights ?? [],
    [families, activeFamily],
  );

  useEffect(() => {
    activeWeights.forEach((weight) => {
      const face = { fontFamily: activeFamily, fontWeight: weight };
      loadCaptionFont(face, findFaceUrl(fonts, face));
    });
  }, [fonts, activeFamily, activeWeights]);

  const closeAll = useCallback(() => {
    setOpen(false);
    setActiveFamily(null);
    setSubmenuAnchor(null);
  }, []);

  // Poppers have no backdrop, so close on a click outside all three areas.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = ({ target }) => {
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target) ||
        submenuRef.current?.contains(target)
      )
        return;
      closeAll();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, closeAll]);

  const hoverFamily = (event, family) => {
    setActiveFamily(family);
    setSubmenuAnchor(event.currentTarget);
  };

  const closeSubmenu = () => {
    setActiveFamily(null);
    setSubmenuAnchor(null);
  };

  const pickWeight = (family, weight) => {
    onChange({ family, weight });
    closeAll();
  };

  // A stored style may carry a weight outside the catalogue; show no label
  // rather than an empty translation.
  const weightLabelKey = CAPTION_FONT_WEIGHT_LABELS[selectedWeight];

  return (
    <>
      <StyledSelectTrigger
        ref={triggerRef}
        $width={width}
        onClick={() => (open ? closeAll() : setOpen(true))}
      >
        <span className="label" style={{ fontFamily: selectedFamily }}>
          {selectedFamily}
        </span>
        {weightLabelKey && <span className="weight">{t(weightLabelKey)}</span>}
        <KeyboardArrowDownRoundedIcon className="arrow" />
      </StyledSelectTrigger>

      <Popper
        open={open}
        anchorEl={triggerRef.current}
        placement="bottom-start"
        style={{ zIndex: 1400, width: width || undefined }}
      >
        <Paper ref={menuRef} sx={MENU_PAPER_SX}>
          <MenuList dense disablePadding>
            {families.map(({ family, defaultWeight, weights }) => {
              const single = weights.length === 1;
              return (
                <MenuItem
                  key={family}
                  selected={family === selectedFamily}
                  onMouseEnter={(event) =>
                    single ? closeSubmenu() : hoverFamily(event, family)
                  }
                  // No submenu opened: the family's own default is the pick.
                  onClick={() => pickWeight(family, defaultWeight)}
                  sx={MENU_ITEM_SX}
                >
                  <span style={{ fontFamily: family }}>{family}</span>
                  {!single && (
                    <ChevronRightRoundedIcon fontSize="small" sx={CHEVRON_SX} />
                  )}
                </MenuItem>
              );
            })}
          </MenuList>
        </Paper>
      </Popper>

      <Popper
        open={Boolean(activeFamily)}
        anchorEl={submenuAnchor}
        placement="right-start"
        style={{ zIndex: 1401 }}
      >
        <Paper ref={submenuRef} sx={SUBMENU_PAPER_SX}>
          <MenuList dense disablePadding>
            {activeWeights.map((weight) => (
              <MenuItem
                key={weight}
                selected={
                  activeFamily === selectedFamily && weight === selectedWeight
                }
                onClick={() => pickWeight(activeFamily, weight)}
                sx={MENU_ITEM_SX}
                style={{ fontFamily: activeFamily, fontWeight: weight }}
              >
                {t(CAPTION_FONT_WEIGHT_LABELS[weight])}
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popper>
    </>
  );
};

export default FontFamilySelect;
